/**
 * Micro-Enterprise Finance (MEF) Insert API Service
 * Handles micro-enterprise financing application submission
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/mefInsertApi.ts
 * Logic preserved AS-IS, using Redux instead of SolidJS signals
 */

import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { API_RESPONSE_CODES, OPERATION_IDS } from "./constants";
import { IMefInsertApiRequest, IMefInsertApiResponse } from "../types/mefInsertTypes";

/**
 * Insert micro-enterprise finance application
 *
 * @param data - Micro-enterprise finance application request data
 * @returns Promise with MEF insert response
 *
 * @example
 * ```typescript
 * const response = await mefInsertApi({
 *   mobileNo: "9876543210",
 *   journeyName: "MEF_FRESH",
 *   businessType: "Retail",
 *   loanAmount: "200000"
 * });
 * ```
 */
export const mefInsertApi = async (data: IMefInsertApiRequest): Promise<IMefInsertApiResponse> => {
  try {
    const response = (await request({
      operationId: OPERATION_IDS.mefInsert,
      requestData: { ...data },
    })) as IMefInsertApiResponse;

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    logPageActionToNewRelic("MEF_INSERT_ERROR", {
      component: "Mef Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    return {} as IMefInsertApiResponse;
  }
};
